<template>
  <component 
    class=" disabled:cursor-default cursor-pointer border border-solid border-transparent transition font-medium ease-linear duration-100 rounded-md py-2 px-4 leading-default disabled:opacity-60 inline-block outline-none text-nowrap"
    :is="computedTag" 
    :class="classes" 
    v-bind="computedAttrs" 
    @click="onClick"
  >
    <slot v-if="!showSpinner"></slot>
    <svg 
      v-else
      aria-hidden="true" 
      class="svg-inline--fa fa-spinner fa-spin bg-transparent fill-white mx-5" 
      viewBox="0 0 100 101" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
    >
      <path 
        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" 
        fill="currentFill" 
      />
    </svg>
  </component>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import type { PropType } from 'vue';
import { RouterLink } from 'vue-router';
import type { RouteLocationRaw } from 'vue-router';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'light' | 'outline' | 'outline-secondary' | 'outline-gray-secondary' | 'transparent';

export default defineComponent({
  name: 'FButton',
  props: {
    variant: {
      type: String as PropType<ButtonVariant>,
      required: false,
      default: 'secondary',
    },
    to: {
      type: [Object, String] as PropType<RouteLocationRaw | null>,
      required: false,
      default: null,
    },
    href: {
      type: String as PropType<string | null>,
      required: false,
      default: null,
    },
    disabled: {
      type: [Boolean, String] as PropType<boolean | string>,
      required: false,
      default: false,
    },
    spinner: {
      type: [Boolean, String] as PropType<boolean | string>,
      required: false,
      default: false,
    },
  },
  emits: ['click'],
  setup(props, { emit }) {
    const isRouterLink = computed<boolean>(() => props.to !== null);
    const isButton = computed<boolean>(() => props.href === null && props.to === null);
    const isNonStandardTag = computed<boolean>(() => props.href !== null ? false : !isButton.value);
    const isLink = computed<boolean>(() => !!(props.to || props.href));
    const resolveBooleanish = (input: unknown) => typeof input === 'boolean' ? input : input === '' ? true : input === 'true' ? true : false;
    const isDisabled = computed(() => props.disabled === undefined || props.disabled === null ? props.disabled : resolveBooleanish(props.disabled));
    const showSpinner = computed(() => props.spinner === undefined || props.spinner === null ? props.spinner : resolveBooleanish(props.spinner));

    const classes = computed(() => {
      switch (props.variant) {
        case 'primary':
          return ['bg-link', 'hover:bg-link-400', 'text-white', 'disabled:hover:bg-link-500', 'hover:brightness-110', 'disabled:brightness-100'];
        case 'secondary':
          return ['bg-white', 'hover:bg-gray-200', 'text-default', 'disabled:hover:bg-white'];
        case 'danger':
          return ['bg-red-500', 'hover:bg-red-400', 'text-white', 'disabled:hover:bg-red-500'];
        case 'light':
          return ['bg-white', 'hover:text-gray-800', 'text-default', 'disabled:hover:bg-white'];
        case 'outline':
          return ['bg-white', 'hover:brightness-110', 'border-solid', '!border-link', 'border', 'text-link', 'disabled:hover:bg-white'];
        case 'outline-secondary':
          return ['bg-white', 'hover:bg-gray-200', 'border-solid', '!border-gray-200', 'text-default', 'border', 'disabled:hover:bg-white'];
        case 'outline-gray-secondary':
          return ['bg-gray-50', 'hover:bg-gray-200', 'border-solid', '!border-gray-200', 'text-default', 'border', 'disabled:hover:bg-white'];
        case 'transparent':
          return ['bg-transparent', 'text-default', 'hover:text-gray-600'];
      }

      return [];
    });

    const computedTag = computed<string | typeof RouterLink>(() =>
      isRouterLink.value ? RouterLink : props.href ? 'a' : 'button'
    );

    const computedAttrs = computed(() => ({
      'aria-disabled': isNonStandardTag.value ? isDisabled.value : null,
      'href': props.href,
      'disabled': isButton.value ? props.disabled : null,
      'to': computedTag.value !== 'button' ? props.to : null,
      'role': isLink.value ? 'button' : null,
    }));

    const onClick = (e: MouseEvent): void => {
      if (props.disabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      emit('click', e);
    };

    return {
      classes,
      computedTag,
      computedAttrs,
      onClick,
      showSpinner,
    };
  },
});
</script>